// components/portal/InventoryList.tsx
'use client';

import { memo, useCallback, useEffect, useMemo, useRef, type WheelEvent } from 'react';
import clsx from 'clsx';
import type {
  InventoryEntityState,
  InventoryEntityType,
  Pagination,
} from '#types/types';
import {
  useInventoryList,
  type UseInventoryListProps,
} from './useInventoryList';

export interface InventoryListRenderState<K extends InventoryEntityType> {
  viewItems: InventoryEntityState<K>[];
  viewIsLoading: boolean;
  refreshIsLoading: boolean;
  viewPagination: Pagination<K> | null;
  viewFilters: Record<string, unknown>;
  hasLockedFilters: boolean;
  isFilterKeyLocked: (key: string) => boolean;
  mergeWithLockedFilters: (candidate: Record<string, unknown>) => Record<string, unknown>;
  setFilters: (next: Record<string, unknown>) => void;
  handleSearchChange: (value: string) => void;
  handleSortChange: (field: string) => void;
  handleManualRefresh: () => void;
  handleReachBottom: () => void;
}

export interface InventoryListProps<K extends InventoryEntityType>
  extends UseInventoryListProps<K> {
  wrapperClassName?: string;
  contentClassName?: string;
  observerRootMargin?: string;
  observerThreshold?: number;
  itemPredicate?: (item: InventoryEntityState<K>) => boolean;
  onExhaustedScrollAttempt?: (state: {
    hasMore: boolean;
    isLoading: boolean;
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  }) => void;
  renderList: (state: InventoryListRenderState<K>) => React.ReactNode;
}

const InventoryListInner = <K extends InventoryEntityType>({
  entityType,
  pageSize,
  initialFilters,
  lockedFilters,
  scopedIds,
  sortOptions,
  filterOptions,
  disableManualRefresh,
  onManualRefresh,
  wrapperClassName,
  contentClassName,
  observerRootMargin = '0px 0px 160px 0px',
  observerThreshold = 0.1,
  itemPredicate,
  onExhaustedScrollAttempt,
  renderList,
}: InventoryListProps<K>) => {
  const {
    viewItems,
    viewIsLoading,
    refreshIsLoading,
    viewPagination,
    viewFilters,
    hasLockedFilters,
    isFilterKeyLocked,
    mergeWithLockedFilters,
    setFilters,
    handleSearchChange,
    handleSortChange,
    handleManualRefresh,
    handleReachBottom,
  } = useInventoryList({
    entityType,
    pageSize,
    initialFilters,
    lockedFilters,
    scopedIds,
    sortOptions,
    filterOptions,
    disableManualRefresh,
    onManualRefresh,
  });

  const displayItems = useMemo(
    () => (itemPredicate ? viewItems.filter((item) => itemPredicate(item)) : viewItems),
    [itemPredicate, viewItems]
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (viewIsLoading) return;
        if (viewPagination?.hasMore === false) return;
        handleReachBottom();
      },
      {
        root,
        rootMargin: observerRootMargin,
        threshold: observerThreshold,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [
    handleReachBottom,
    observerRootMargin,
    observerThreshold,
    viewIsLoading,
    viewPagination?.hasMore,
  ]);

  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    if (!onExhaustedScrollAttempt) return;
    if (event.deltaY <= 0) return;
    const node = scrollRef.current;
    if (!node) return;

    const scrollTop = node.scrollTop;
    const scrollHeight = node.scrollHeight;
    const clientHeight = node.clientHeight;
    const remaining = scrollHeight - (scrollTop + clientHeight);

    if (remaining > 8) return;

    onExhaustedScrollAttempt({
      hasMore: viewPagination?.hasMore === true,
      isLoading: viewIsLoading,
      scrollTop,
      scrollHeight,
      clientHeight,
    });
  }, [onExhaustedScrollAttempt, viewIsLoading, viewPagination?.hasMore]);

  return (
    <div
      ref={scrollRef}
      onWheel={handleWheel}
      className={clsx('flex-1 overflow-y-auto custom-scrollbar', wrapperClassName)}
    >
      <div className={clsx(contentClassName)}>
        {renderList({
          viewItems: displayItems,
          viewIsLoading,
          refreshIsLoading,
          viewPagination,
          viewFilters: viewFilters as Record<string, unknown>,
          hasLockedFilters,
          isFilterKeyLocked,
          mergeWithLockedFilters,
          setFilters,
          handleSearchChange,
          handleSortChange,
          handleManualRefresh,
          handleReachBottom,
        })}
        <div ref={sentinelRef} className="h-4 w-full pointer-events-none" />
      </div>
    </div>
  );
};

export const InventoryList = memo(InventoryListInner) as typeof InventoryListInner;
